import { print } from 'graphql';
import { MessageTypes } from '@dva/frameio-uxp-messaging';
import { CONFIG } from '@frameio/config';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { auth, hasAccessTokenExpired } from '@frameio/utils/auth';
import { getCookie } from '@frameio/utils/cookies';
import { Authorization } from '../../generated';
import { getOperationNameFromDocumentNode } from '../../utils/getOperationNameFromDocumentNode';
import { getSharedUXPLink } from './UXPLink';

declare global {
  interface Window {
    uxpHost?: {
      postMessage: (message: unknown, targetOrigin?: string) => void;
    };
  }
}

const REFRESH_ACCESS_TOKEN_MUTATION = `
  mutation RefreshAccessTokenViaUXP {
    refreshAccessToken
  }
`;

/**
 * Centralized UXP token refresh utility that uses Web Locks API to prevent race conditions.
 *
 * This utility coordinates token refresh requests from multiple sources (UXPRefreshLink,
 * useAttemptAuthRefresh hook, etc.) to ensure only one refresh operation happens at a time.
 *
 * @param options - Configuration options
 * @param options.timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns Promise that resolves to true if authentication succeeds, or false otherwise
 */
export const handleUXPRefresh = async (
  options: {
    timeoutMs?: number;
  } = {}
): Promise<boolean> => {
  const { timeoutMs = 10000 } = options;

  const token: string | undefined = auth.getAccessToken();

  // bail early if the token is valid.
  if (token && !hasAccessTokenExpired(token)) {
    return true;
  }

  if (typeof window === 'undefined' || !window.uxpHost) {
    throw new Error('handleUXPRefresh can only be used in UXP environment');
  }

  return navigator.locks.request(
    'UXP_REFRESH_ACCESS_TOKEN_LOCK',
    {
      mode: 'exclusive',
    },
    async () => {
      const currentToken = auth.getAccessToken();
      if (currentToken && !hasAccessTokenExpired(currentToken)) {
        return true;
      }

      const accessToken = await refreshAccessTokenViaUXP({ timeoutMs });
      if (accessToken) {
        auth.setTokens({ access: accessToken });
        const isAuthVerified = await verifyAuthSuccess(accessToken);
        return isAuthVerified;
      }
      return false;
    }
  );
};

/**
 * Internal function that performs the actual UXP refresh using the shared UXPLink instance
 */
const refreshAccessTokenViaUXP = async (options: {
  timeoutMs: number;
}): Promise<string | undefined> => {
  const { timeoutMs } = options;

  if (!window.uxpHost) {
    throw new Error('UXP host not available');
  }

  try {
    const uxpLink = getSharedUXPLink();
    const result = await uxpLink.sendMessage(
      {
        type: MessageTypes.GRAPHQL_REQUEST,
        operation: REFRESH_ACCESS_TOKEN_MUTATION,
        operationName: 'RefreshAccessTokenViaUXP',
        variables: {},
      },
      { timeoutMs }
    );

    const accessToken: string | undefined = result?.data?.refreshAccessToken;

    if (accessToken) {
      return accessToken;
    }

    throw new Error('Invalid refreshAccessToken response');
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown refresh error';
    datadogLogger.warn('uxp_refresh_token_failed', {
      location: 'handleUXPRefresh',
      error: errorMessage,
    });
    throw error;
  }
};

/**
 * Verifies that the auth is successful by checking if the user is authenticated.
 * @returns Promise<boolean> - True if the auth is successful, false otherwise.
 */
const verifyAuthSuccess = async (accessToken: string): Promise<boolean> => {
  const clientName = CONFIG.thirdParty.apolloClient?.name;
  const clientVersion = CONFIG.clientIdentifierVersion;

  const operationName = getOperationNameFromDocumentNode(Authorization);
  const previewEnvironment = getCookie('x-preview-environment');
  const fetchResponse = await fetch(CONFIG.graphqlUrl, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'apollographql-client-name': clientName!,
      'apollographql-client-version': clientVersion!,
      'x-gql-op': operationName,
      ...(previewEnvironment && {
        'x-preview-environment': previewEnvironment,
      }),
    } as const,
    body: JSON.stringify({
      operationName,
      query: print(Authorization),
    }),
  });
  const data = await fetchResponse.json();
  return (
    fetchResponse.ok && !data?.errors?.length && Boolean(data?.data?.me?.id)
  );
};
